import { Button } from "@repo/ui/components/Base/Button";
import { useEffect } from "react";

interface TablePaginationProps {
  pageSize: number;
  pageIndex: number; // 0-based
  totalRows: number;
  onPageSizeChange: (size: number) => void;
  onPageChange: (pageIndex: number) => void;
  pageSizes?: number[];
}

export const TablePagination = ({
  pageSize,
  pageIndex,
  totalRows,
  onPageSizeChange,
  onPageChange,
  pageSizes = [10, 20, 30, 40, 50],
}: TablePaginationProps) => {
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize));
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < pageCount - 1;

  useEffect(() => {
    const clamped = Math.min(pageIndex, pageCount - 1);
    if (clamped !== pageIndex) onPageChange(clamped);
  }, [pageCount, pageIndex, onPageChange]);

  const handlePageSizeChange = (newSize: number) => {
    const newPageCount = Math.max(1, Math.ceil(totalRows / newSize));
    const clamped = Math.min(pageIndex, newPageCount - 1);
    onPageSizeChange(newSize);
    if (clamped !== pageIndex) onPageChange(clamped);
  };

  const goToFirstPage = () => onPageChange(0);
  const goToPreviousPage = () => onPageChange(pageIndex - 1);
  const goToNextPage = () => onPageChange(pageIndex + 1);
  const goToLastPage = () => onPageChange(pageCount - 1);

  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-2 text-sm">
        Show{" "}
        <select
          value={pageSize}
          className="p-1 rounded-md border border-gray-300"
          onChange={(e) => handlePageSizeChange(Number(e.target.value))}
        >
          {pageSizes.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        of {totalRows} rows.
      </div>

      <div className="flex items-center justify-end space-x-2 text-sm">
        <Button
          variant="ghost"
          size="sm"
          onClick={goToFirstPage}
          disabled={!canPreviousPage}
        >
          &laquo;
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={goToPreviousPage}
          disabled={!canPreviousPage}
        >
          &#8249;
        </Button>
        <span>
          Page {pageIndex + 1} of {pageCount}.
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={goToNextPage}
          disabled={!canNextPage}
        >
          &#8250;
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={goToLastPage}
          disabled={!canNextPage}
        >
          &raquo;
        </Button>
      </div>
    </div>
  );
};
